// Per-project database connections panel (Database Designer → "Connections").
//
// Lets the user define named database connections (Postgres / Oracle /
// SQLite): one optional "Use for Preview & Run" connection that replaces the
// tenant preview DB for THIS project, plus additional connections reserved
// for future use and external-application data sync. Shows which database
// preview/run currently resolves to.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import {
  bizLogicAllowedOn,
  deleteProjectDbConnection,
  DEFAULT_BIZ_LOGIC,
  getProjectBusinessLogicSummary,
  getResolvedPreviewDb,
  listProjectDbConnections,
  putEngineSettings,
  saveProjectDbConnection,
  testProjectDbConnection,
  type AdoptionMode,
  type BusinessLogicSummary,
  type ProjectDbConnection,
  type ProjectDbConnectionInput,
  type ProjectDbRole,
  type ProjectDbType,
  type ResolvedPreviewDb,
} from '@/api/projectDb'

// Details of a database-dialect switch that requires migrating existing
// generated business logic to the new engine.
export interface DialectMigration {
  from: ProjectDbType
  to: ProjectDbType
  /** Human-readable summary of the new target database. */
  summary: string
  functionCount: number
  languages: string[]
  objects: string[]
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  margin: '10px 0 4px',
  color: 'var(--color-text-secondary)',
}

const TYPE_LABEL: Record<ProjectDbType, string> = {
  postgres: 'Postgres',
  oracle: 'Oracle',
  sqlite: 'SQLite',
}

const ROLE_LABEL: Record<ProjectDbRole, string> = {
  preview_runtime: 'Preview & Run',
  general: 'General (future use)',
  sync_target: 'Sync target',
}

const PLATFORM_DEFAULT_SUMMARY = 'Tenant database (platform default)'

interface EditState extends ProjectDbConnectionInput {
  hasStoredPassword?: boolean
}

// Human-readable one-line summary of a connection draft (for the sync offer).
function summarizeDraft(d: EditState): string {
  const label = TYPE_LABEL[d.db_type]
  if (d.db_type === 'sqlite') {
    return `${label} — ${d.sqlite_path || '(default project file)'} (connection "${d.name}")`
  }
  const target = `${d.username || ''}@${d.host || ''}:${d.port || ''}/${d.database_name || ''}`
  return `${label} — ${target} (connection "${d.name}")`
}

function emptyDraft(project: string): EditState {
  return {
    project,
    name: '',
    db_type: 'postgres',
    role: 'general',
    adoption_mode: 'source',
    oracle_connect_type: 'service_name',
  }
}

export default function ConnectionsPanel({
  project, open, onClose, onPreviewDbChanged, onDialectMigration,
}: {
  project: string
  open: boolean
  onClose: () => void
  /**
   * Fired after the Preview & Run database actually changes (a connection
   * promoted/edited as preview_runtime, or such a connection deleted). The
   * parent uses this to offer a table sync to the new database. Receives a
   * human-readable summary of the new target.
   *
   * NOT fired when the change is a dialect switch that has generated business
   * logic to migrate — that fires onDialectMigration instead, since the
   * Ask-AI migration plan handles the table rebuild itself.
   */
  onPreviewDbChanged?: (summary: string) => void
  /**
   * Fired when the Preview & Run database is switched to a DIFFERENT engine
   * (e.g. Postgres → Oracle) AND the project has generated business logic that
   * must be migrated to the new dialect. The parent hands this off to the
   * Ask-AI agent to plan and execute the migration.
   */
  onDialectMigration?: (migration: DialectMigration) => void
}) {
  const [connections, setConnections] = useState<ProjectDbConnection[]>([])
  const [resolved, setResolved] = useState<ResolvedPreviewDb | null>(null)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState<EditState | null>(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [conns, res] = await Promise.all([
        listProjectDbConnections(project),
        getResolvedPreviewDb(project),
      ])
      setConnections(conns)
      setResolved(res)
    } catch (err) {
      toast.error(`Could not load connections: ${err instanceof Error ? err.message : err}`)
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  const patch = (p: Partial<EditState>) =>
    setDraft((d) => (d ? { ...d, ...p } : d))

  // A stored connection row as a full save payload (password omitted →
  // the backend keeps the stored one).
  const rowToInput = (c: ProjectDbConnection): EditState => ({
    project,
    id: c.id,
    name: c.name,
    db_type: c.db_type,
    role: c.role,
    // Carried through explicitly: a save omitting it would silently reset the
    // mode to the default on every edit of an unrelated field.
    adoption_mode: c.adoption_mode ?? 'source',
    host: c.host ?? undefined,
    port: c.port ?? undefined,
    database_name: c.database_name ?? undefined,
    oracle_connect_type: (c.oracle_connect_type as 'service_name' | 'sid') ?? 'service_name',
    username: c.username ?? undefined,
    sqlite_path: c.sqlite_path ?? undefined,
    db_schema: c.db_schema ?? undefined,
    hasStoredPassword: c.has_password,
  })

  const startEdit = (c: ProjectDbConnection) => setDraft(rowToInput(c))

  // Save a connection, handling every way the save can MOVE the Preview & Run
  // target: promoting `next` to preview_runtime, demoting the active
  // preview_runtime connection (preview reverts to the platform default
  // tenant Postgres), or switching database dialect (which may require an
  // Ask-AI business-logic migration). Used by the edit form's Save, the
  // per-row "Use for Preview & Run" and the "Use platform default" actions.
  const saveWithChecks = async (
    next: EditState, opts: { clearDraft?: boolean } = {},
  ) => {
    const isPreview = next.role === 'preview_runtime'
    const wasPreview = connections.some(
      (c) => c.id === next.id && c.role === 'preview_runtime',
    )
    // The dialect preview/run will use AFTER this save: the connection's own
    // (promote/keep), postgres (demote → platform default), or unchanged.
    const targetDialect: ProjectDbType | null = isPreview
      ? next.db_type
      : wasPreview ? 'postgres' : null
    // The dialect preview/run currently resolves to (tenant default = postgres).
    const currentDialect = resolved?.db_type
    const dialectChange =
      !!targetDialect && !!currentDialect && targetDialect !== currentDialect

    // A dialect switch may have generated business logic written for the old
    // engine that needs migrating — fetch the summary up-front so we can warn.
    let bl: BusinessLogicSummary | null = null
    if (dialectChange) {
      try {
        bl = await getProjectBusinessLogicSummary(project)
      } catch {
        bl = null
      }
    }
    const needsMigration = dialectChange && !!bl?.has_generated_logic
    const newSummary = isPreview ? summarizeDraft(next) : PLATFORM_DEFAULT_SUMMARY

    if (needsMigration && bl && targetDialect) {
      const fromL = TYPE_LABEL[currentDialect as ProjectDbType]
      const toL = TYPE_LABEL[targetDialect]
      const target = isPreview ? 'this database' : 'the platform default tenant database'
      const fnWord = bl.function_count === 1 ? 'function' : 'functions'
      const objWord = bl.objects.length === 1 ? 'object' : 'objects'
      if (!window.confirm(
        `Switch Preview & Run to ${target} (${fromL} → ${toL})?\n\n`
        + `This project has ${bl.function_count} generated business-logic `
        + `${fnWord} (${bl.languages.join(', ')}) across ${bl.objects.length} `
        + `${objWord} that were written for ${fromL} and must be migrated to ${toL}.\n\n`
        + `When you switch, Ask-AI will create a migration plan and run it — `
        + `translating the business logic to ${toL}, switching the project's `
        + `business-logic language, and rebuilding the tables on the new database.\n\n`
        + 'Existing preview data is NOT migrated.',
      )) return
    } else if (isPreview) {
      const prev = connections.find(
        (c) => c.role === 'preview_runtime' && c.id !== next.id,
      )
      const movingTarget = prev || connections.every((c) => c.id !== next.id)
      if (movingTarget && !window.confirm(
        'Switch Preview & Run to this database?\n\n'
        + 'Existing preview data is NOT migrated — it stays in the previous '
        + 'database, and tables are recreated empty in the new one on the '
        + 'next deploy.',
      )) return
    } else if (wasPreview && !window.confirm(
      'Stop using this database for Preview & Run?\n\n'
      + 'Preview & Run revert to the platform default tenant database. '
      + 'Existing data stays in this database — it is NOT migrated, and '
      + 'tables are recreated empty in the default database on the next '
      + 'deploy. The connection itself is kept (as "General").',
    )) return
    setSaving(true)
    // True once we've realigned the engine's db_mode/biz_logic for the new
    // dialect — so we can roll it back if the connection save then fails.
    let engineRealigned = false
    try {
      // A dialect switch must keep the project's business-logic language valid
      // for the new engine, otherwise the backend rejects the connection (a
      // preview_runtime db_type must be compatible with biz_logic). Keep the
      // current language if it still works on the new engine, else fall back
      // to that engine's natural default. The Ask-AI migration then rewrites
      // the actual function code to match.
      if (dialectChange && targetDialect) {
        const keep = !!bl && bizLogicAllowedOn(targetDialect, bl.current_biz_logic)
        const newBiz = keep ? bl!.current_biz_logic : DEFAULT_BIZ_LOGIC[targetDialect]
        await putEngineSettings(project, { db_mode: targetDialect, biz_logic: newBiz })
        engineRealigned = true
      }
      const { hasStoredPassword: _ignored, ...body } = next
      await saveProjectDbConnection(body)
      toast.success(
        isPreview
          ? `Preview & Run now use "${next.name}"`
          : wasPreview
            ? 'Preview & Run reverted to the platform default'
            : `Connection "${next.name}" saved`,
      )
      if (opts.clearDraft) setDraft(null)
      await refresh()
      if (needsMigration && bl && targetDialect) {
        // Hand the migration off to the Ask-AI agent (it rebuilds the tables
        // itself, so we do NOT also open the plain table-sync flow).
        onDialectMigration?.({
          from: currentDialect as ProjectDbType,
          to: targetDialect,
          summary: newSummary,
          functionCount: bl.function_count,
          languages: bl.languages,
          objects: bl.objects,
        })
      } else if (isPreview || wasPreview) {
        // The preview/run database changed → let the parent offer a table sync.
        onPreviewDbChanged?.(newSummary)
      }
    } catch (err: unknown) {
      // The connection save failed after we realigned the engine for the new
      // dialect — roll the engine settings back to the old dialect so the
      // project isn't left pointing at an engine it has no connection for.
      if (engineRealigned && currentDialect) {
        try {
          await putEngineSettings(project, {
            db_mode: currentDialect,
            biz_logic: bl?.current_biz_logic || DEFAULT_BIZ_LOGIC[currentDialect],
          })
        } catch { /* best effort */ }
      }
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || `Save failed: ${err instanceof Error ? err.message : err}`)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (draft) await saveWithChecks(draft, { clearDraft: true })
  }

  // Promote an existing connection to be THE project database (Preview & Run).
  const handleUseForPreview = (c: ProjectDbConnection) =>
    saveWithChecks({ ...rowToInput(c), role: 'preview_runtime' })

  // Demote the active Preview & Run connection — preview reverts to the
  // platform default tenant database; the connection is kept as "General".
  const handleUseDefault = (c: ProjectDbConnection) =>
    saveWithChecks({ ...rowToInput(c), role: 'general' })

  const handleDelete = async (c: ProjectDbConnection) => {
    const warn = c.role === 'preview_runtime'
      ? '\n\nThis is the Preview & Run database — preview will revert to the default tenant database.'
      : ''
    if (!window.confirm(`Delete connection "${c.name}"?${warn}`)) return
    try {
      await deleteProjectDbConnection(project, c.id)
      toast.success(`Deleted "${c.name}"`)
      await refresh()
      // Removing the preview/run DB reverts to the tenant default — offer a
      // sync so that default database gets the schema.
      if (c.role === 'preview_runtime') {
        onPreviewDbChanged?.('Tenant database (platform default)')
      }
    } catch (err) {
      toast.error(`Delete failed: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleTest = async () => {
    if (!draft) return
    setTesting(true)
    try {
      const { hasStoredPassword: _ignored, ...body } = draft
      const res = await testProjectDbConnection(
        draft.id && !draft.password ? { ...body, connection_id: draft.id } : body,
      )
      if (res.success) toast.success(res.message)
      else toast.error(res.message)
    } catch (err) {
      toast.error(`Test failed: ${err instanceof Error ? err.message : err}`)
    } finally {
      setTesting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Database connections" size="wide">
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Resolved banner */}
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: '0.875rem',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <strong>Preview &amp; Run uses:</strong>{' '}
          {resolved
            ? `${resolved.summary}${resolved.connection_name ? ` (connection "${resolved.connection_name}")` : ''}`
            : '…'}
          {resolved?.source === 'project_connection' && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
              This database is used strictly — if the connection fails,
              Preview &amp; Run report the error instead of silently falling
              back to the platform default.
            </div>
          )}
        </div>

        {!draft && (
          <>
            {/* Connection list */}
            {loading && connections.length === 0 ? (
              <div className="spinner" />
            ) : connections.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                No connections defined. Preview &amp; Run use the default
                tenant database. Add a connection to run this project on an
                external Postgres, Oracle or SQLite database, or to register
                a database for data sync.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {connections.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8,
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.9125rem', fontWeight: 600 }}>
                        {c.name}
                        <span
                          style={{
                            marginLeft: 8, fontSize: '0.8125rem', fontWeight: 500,
                            padding: '1px 8px', borderRadius: 999,
                            background: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          {TYPE_LABEL[c.db_type]}
                        </span>
                        {c.role === 'preview_runtime' && (
                          <span
                            style={{
                              marginLeft: 6, fontSize: '0.8125rem', fontWeight: 600,
                              padding: '1px 8px', borderRadius: 999,
                              background: 'var(--color-primary)', color: '#fff',
                            }}
                          >
                            Preview &amp; Run
                          </span>
                        )}
                        {c.role === 'sync_target' && (
                          <span
                            style={{
                              marginLeft: 6, fontSize: '0.8125rem', fontWeight: 600,
                              padding: '1px 8px', borderRadius: 999,
                              background: 'var(--color-bg-secondary)',
                              border: '1px solid var(--color-border)',
                            }}
                          >
                            Sync target
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {c.db_type === 'sqlite'
                          ? (c.sqlite_path || '(default project file)')
                          : `${c.username || ''}@${c.host || ''}:${c.port || ''}/${c.database_name || ''}`}
                      </div>
                    </div>
                    {c.role === 'preview_runtime' ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Stop using this database — Preview & Run revert to the platform default tenant database"
                        disabled={saving}
                        onClick={() => void handleUseDefault(c)}
                      >
                        Use platform default
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Make this the project's database — Preview & Run use it instead of the tenant default"
                        disabled={saving}
                        onClick={() => void handleUseForPreview(c)}
                      >
                        Use for Preview &amp; Run
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => startEdit(c)}>
                      Edit
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => void handleDelete(c)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <button className="btn btn-primary btn-sm" onClick={() => setDraft(emptyDraft(project))}>
                + Add connection
              </button>
            </div>
          </>
        )}

        {draft && (
          <div>
            <label style={labelStyle}>Name</label>
            <input
              style={inputStyle}
              value={draft.name}
              placeholder="e.g. prod_oracle"
              onChange={(e) => patch({ name: e.target.value })}
            />

            <label style={labelStyle}>Database type</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={draft.db_type}
              onChange={(e) => patch({ db_type: e.target.value as ProjectDbType })}
            >
              <option value="postgres">Postgres</option>
              <option value="oracle">Oracle (19c+)</option>
              <option value="sqlite">SQLite</option>
            </select>

            <label style={labelStyle}>Used for</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={draft.role}
              onChange={(e) => patch({ role: e.target.value as ProjectDbRole })}
            >
              <option value="general">{ROLE_LABEL.general}</option>
              <option value="preview_runtime">{ROLE_LABEL.preview_runtime}</option>
              <option value="sync_target">{ROLE_LABEL.sync_target}</option>
            </select>
            {draft.role === 'preview_runtime' && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                Preview &amp; Run will use this database instead of the tenant
                preview DB — strictly: if the connection fails, they report
                the error rather than falling back to the tenant default.
                Existing preview data is not migrated.
              </p>
            )}

            <label style={labelStyle}>When building on this database</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={draft.adoption_mode || 'source'}
              onChange={(e) => patch({ adoption_mode: e.target.value as AdoptionMode })}
            >
              <option value="source">Use its tables (this becomes the app database)</option>
              <option value="structure">Reuse only its table structure</option>
            </select>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
              {draft.adoption_mode === 'structure'
                ? 'Only the table design is copied. This project creates and owns '
                  + 'its own tables, so preview and Run use its own data and this '
                  + 'database is never written. Use it when two applications share '
                  + 'a schema but not their rows.'
                : 'The application reads and writes these tables in place — deploy '
                  + 'binds to them and never creates or alters them. Requires the '
                  + '"Preview & Run" role above, since a project has one database.'}
            </p>

            {draft.db_type === 'sqlite' ? (
              <>
                <label style={labelStyle}>File path (optional)</label>
                <input
                  style={inputStyle}
                  value={draft.sqlite_path || ''}
                  placeholder="(default: per-project preview file)"
                  onChange={(e) => patch({ sqlite_path: e.target.value })}
                />
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                  <div>
                    <label style={labelStyle}>Host</label>
                    <input
                      style={inputStyle}
                      value={draft.host || ''}
                      onChange={(e) => patch({ host: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Port</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={draft.port ?? ''}
                      placeholder={draft.db_type === 'oracle' ? '1521' : '5432'}
                      onChange={(e) =>
                        patch({ port: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </div>
                </div>

                {draft.db_type === 'oracle' ? (
                  <>
                    <label style={labelStyle}>Connect by</label>
                    <div style={{ display: 'flex', gap: 16, fontSize: '0.875rem' }}>
                      <label style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={(draft.oracle_connect_type || 'service_name') === 'service_name'}
                          onChange={() => patch({ oracle_connect_type: 'service_name' })}
                        />{' '}
                        Service name
                      </label>
                      <label style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={draft.oracle_connect_type === 'sid'}
                          onChange={() => patch({ oracle_connect_type: 'sid' })}
                        />{' '}
                        SID
                      </label>
                    </div>
                    <label style={labelStyle}>
                      {draft.oracle_connect_type === 'sid' ? 'SID' : 'Service name'}
                    </label>
                    <input
                      style={inputStyle}
                      value={draft.database_name || ''}
                      placeholder="e.g. FREEPDB1"
                      onChange={(e) => patch({ database_name: e.target.value })}
                    />
                  </>
                ) : (
                  <>
                    <label style={labelStyle}>Database</label>
                    <input
                      style={inputStyle}
                      value={draft.database_name || ''}
                      placeholder="Database name (bring-your-own; never created)"
                      onChange={(e) => patch({ database_name: e.target.value })}
                    />
                  </>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input
                      style={inputStyle}
                      value={draft.username || ''}
                      onChange={(e) => patch({ username: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Password</label>
                    <input
                      style={inputStyle}
                      type="password"
                      value={draft.password ?? ''}
                      placeholder={draft.hasStoredPassword ? '(unchanged)' : ''}
                      onChange={(e) => patch({ password: e.target.value })}
                    />
                  </div>
                </div>

                <label style={labelStyle}>Schema (optional)</label>
                <input
                  style={inputStyle}
                  value={draft.db_schema || ''}
                  placeholder={draft.db_type === 'oracle'
                    ? "(default: the connecting user's own schema)"
                    : '(default: twasta — use e.g. public when the data already lives there)'}
                  onChange={(e) => patch({ db_schema: e.target.value })}
                />
                {draft.db_type === 'postgres' && (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                    For an existing external database, set this to the schema
                    that holds its tables (usually <code>public</code>) —
                    otherwise twasta reads/writes its own <code>twasta</code>{' '}
                    schema inside this database.
                  </p>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void handleSave()}
                disabled={saving || !draft.name.trim()}
              >
                {saving ? 'Saving…' : 'Save connection'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void handleTest()}
                disabled={testing}
              >
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setDraft(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
